/**
 * Customer reviews and the public Google aggregate.
 *
 * These are REAL reviews from the live Google Business listing, reproduced
 * verbatim. They are not marketing copy and are deliberately not translated —
 * a customer's own words in their own language is the thing that carries the
 * trust. Editing them for tone, length, or grammar would make them testimonial
 * fiction, which is the one thing this file must never hold.
 *
 * Source of truth for both the home hero's trust card and the testimonials
 * section, so the two can never quote the same business differently.
 */

export interface CustomerReview {
  /** Stable slug, used as a React key and to pick the featured review. */
  readonly id: string;
  readonly content: string;
  readonly author: string;
  /**
   * BCP-47 language of `content`, which is whatever the customer wrote in —
   * not the locale of the page showing it. Carried so the hero can pick a
   * quote its visitor can actually read, and so the markup can declare the
   * quote's language when it differs from the document's.
   */
  readonly lang: "en" | "es";
}

/**
 * The listing the numbers below were read from.
 *
 * Verified 2026-08-25: 4.6 stars across 853 reviews, all Google-sourced.
 */
export const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/place/Miller+40+Car+Care+%26+Tire+Center/@25.7336313,-80.3070954,17z/data=!4m18!1m9!3m8!1s0x88d9b86c2037b817:0xcb2b3ee32c96b6d8!2sMiller+40+Car+Care+%26+Tire+Center!8m2!3d25.7336313!4d-80.3045205!9m1!1b1!16s%2Fg%2F11c52h4rf_!3m7!1s0x88d9b86c2037b817:0xcb2b3ee32c96b6d8!8m2!3d25.7336313!4d-80.3045205!9m1!1b1!16s%2Fg%2F11c52h4rf_?entry=ttu";

/** What the listing actually showed when last checked (2026-08-25). */
export const RATING_VERIFIED = 4.6;
export const REVIEW_COUNT_VERIFIED = 853;

/**
 * What the site claims.
 *
 * The count is rounded DOWN to a round number, so the claim survives without
 * maintenance: reviews accumulate, so an understated floor stays true and only
 * grows more conservative. A live figure would need the Google Places API — a
 * key, a fetch, a cache, and a new failure mode on a page that has none. Revisit
 * that trade when the gap gets embarrassing, not before.
 *
 * `tests/domain/reviews.test.ts` fails if either advertised value ever exceeds
 * what was verified, so bumping one without re-checking the listing is caught.
 */
export const RATING_ADVERTISED = 4.6;
export const REVIEW_COUNT_ADVERTISED = 800;

export const CUSTOMER_REVIEWS: readonly CustomerReview[] = [
  {
    id: "andrea-riveron",
    content:
      "They did an amazing job after I needed repairs from an accident. Transparent, solution-oriented, and I felt taken care of! Thank you!!",
    author: "Andrea Riveron",
    lang: "en",
  },
  {
    id: "james-swindoll",
    content: "Great experience and very kind crew",
    author: "James Swindoll",
    lang: "en",
  },
  {
    id: "delvys-suarez",
    content:
      "Mi experiencia en Miller 40 fue excelente todas las chicas super amables y competentes . Al igual que el resto del equipo . Muy conforme con el servicio prestado",
    author: "Delvys Suarez",
    lang: "es",
  },
] as const;

/**
 * The review that leads the hero, per locale.
 *
 * An editorial choice, not an algorithm, and a per-locale one because the
 * reviews are real and untranslated. The shortest quote here says nothing a
 * visitor could not assume ("Great experience and very kind crew"), and the
 * only Spanish review is the wrong thing to greet an en-US visitor with —
 * exactly as the English ones are wrong for an es-US visitor. Naming an id per
 * locale keeps both judgements visible instead of burying them in a sort.
 */
const FEATURED_REVIEW_BY_LOCALE: Record<string, string> = {
  "en-US": "andrea-riveron",
  "es-US": "delvys-suarez",
};

/** The locale whose pick stands in for any locale not named above. */
export const DEFAULT_FEATURED_LOCALE = "en-US";

/**
 * The featured review for `locale`, or `null` if it is no longer in the list.
 *
 * Null rather than undefined-and-render, so removing a review degrades the
 * hero to a rating-only card instead of printing "undefined" into it; and null
 * rather than a throw, because this resolves inside a Server Component where an
 * exception costs the whole home page.
 */
export function getFeaturedReview(
  locale: string,
  reviews: readonly CustomerReview[] = CUSTOMER_REVIEWS,
): CustomerReview | null {
  const id =
    FEATURED_REVIEW_BY_LOCALE[locale] ??
    FEATURED_REVIEW_BY_LOCALE[DEFAULT_FEATURED_LOCALE];
  return reviews.find((review) => review.id === id) ?? null;
}
